import BasicSection from 'components/BasicSection';
import CalendarGroup from 'components/CalendarGroup';
import GoogleButton from 'components/GoogleButton';
import LinkButton from 'components/LinkButton';
import PanelContainer from 'components/PanelContainer';
import {ANIMATION_TIMEOUT_APPEAR} from 'constants/animation.js';
import {ENDPOINT} from 'constants/endpoints';
import {PERMISSIONS} from 'constants/permissions';
import {USER_OPTIONS} from 'constants/user-options';
import {TimeHelpers} from 'helpers/date-helpers';
import {useApi} from 'hooks/useApi';

import './styles.css';
import {useWindowRef} from 'hooks/useWindowRef';

import PageLayout from 'layouts/PageLayout';

import {Schedule} from 'pages/Calendar/helper.js';
import React, {useCallback, useEffect, useState} from 'react';
import {CSSTransition} from 'react-transition-group';
import ConfirmDialog from 'scenes/Dialog/components/ConfirmDialog';
import ResponseErrorDialog from 'scenes/Dialog/components/ResponseErrorDialog';
import CalendarWorkingHoursForm from 'scenes/Form/components/CalendarWorkingHoursForm';
import BasicSelect from '../../components/BasicSelect';
import BasicTitle from '../../components/BasicTitle';
import MicrosoftButton from '../../components/MicrosoftButton';

const TIME_ZONES = [
  {value: 'EST', label: 'Eastern Time'},
  {value: 'MST', label: 'Mountain Time'},
  {value: 'PST', label: 'Pacific Time'},
  {value: 'CST', label: 'Central Standard Time'},
];

const Calendar = props => {
  const { globalActions } = props;
  const [duration, setDuration] = useState();
  const [schedule, setSchedule] = useState();
  const [calendar, setCalendar] = useState({});
  const [editableInterval, setEditableInterval] = useState();
  const [freeIntervals, setFreeIntervals] = useState();

  const api = useApi();

  const loadData = () =>
    api.get(
      ENDPOINT.ENTITY.RECRUITER_CALENDAR,
      {},
      {
        handleSuccess: ({ calendar, duration, schedule }) => {
          if (calendar) {
            setSchedule(Schedule.prepareStore(schedule));
            setDuration(duration);
            setCalendar(calendar);
          }
        },
      }
    );

  const [setWindowRef] = useWindowRef(message => {
    message.success === 'ERROR' &&
      message.data.message &&
      ResponseErrorDialog({ response: message });
    if (message.success === 'OK') {
      globalActions.updateUserOption(USER_OPTIONS.HAS_RECRUITER_CALENDAR, true);
      globalActions.updateUserPermission(PERMISSIONS.PERSONAL_CALENDAR, true);
      loadData();
    }
  });

  useEffect(() => {
    loadData();
  }, []);

  const postRequest = (endpoint, params, callback) => {
    api.post(endpoint, params, {
      handleError: response => ResponseErrorDialog({ response }),
      handleSuccess: callback,
    });
  };

  const handleChangeDayStatus = (Dow, isActive) => {
    const updatedSchedule = new Schedule(schedule);
    updatedSchedule.changeDayStatus(Dow, isActive);

    const endpoint = isActive
      ? ENDPOINT.ACTION.RECRUITER_CALENDAR.DAY_ON
      : ENDPOINT.ACTION.RECRUITER_CALENDAR.DAY_OFF;

    postRequest(endpoint, { DayOfWeek: Dow }, () => {
      if (editableInterval && editableInterval.Dow === Dow) {
        setEditableInterval(null);
      }
      setSchedule(updatedSchedule.getStore());
    });
  };

  const handleSaveInterval = interval => {
    const updatedSchedule = new Schedule(schedule);
    updatedSchedule.deselectInterval();

    const isNew = !interval.ScheduleID;
    const endpoint = isNew
      ? ENDPOINT.ACTION.RECRUITER_CALENDAR.INTERVAL_CREATE
      : ENDPOINT.ACTION.RECRUITER_CALENDAR.INTERVAL_UPDATE;

    const params = {
      Active: interval.Active,
      DayOfWeek: interval.Dow,
      TimeStart: TimeHelpers.secondsToTime(interval.TimeStart.value),
      TimeEnd: TimeHelpers.secondsToTime(interval.TimeEnd.value),
    };

    if (!isNew) {
      params.ScheduleID = interval.ScheduleID;
    }

    postRequest(endpoint, params, response => {
      setEditableInterval(null);
      setFreeIntervals(null);
      updatedSchedule.setInterval({
        ...interval,
        ScheduleID: isNew ? response.ScheduleID : interval.ScheduleID,
      });
      setSchedule(updatedSchedule.getStore());
    });
  };

  const handleDeleteInterval = interval => {
    const updatedSchedule = new Schedule(schedule);
    updatedSchedule.removeInterval(editableInterval);

    postRequest(
      ENDPOINT.ACTION.RECRUITER_CALENDAR.INTERVAL_REMOVE,
      { id: interval.ScheduleID },
      () => {
        setEditableInterval(null);
        setFreeIntervals(null);
        setSchedule(updatedSchedule.getStore());
      }
    );
  };

  const handleAddInterval = Dow => {
    const updatedSchedule = new Schedule(schedule);
    updatedSchedule.deselectInterval();

    setFreeIntervals(updatedSchedule.getFreeIntervals(Dow, duration));
    setEditableInterval({ Dow, Active: true, TimeStart: null, TimeEnd: null });
    setSchedule(updatedSchedule.getStore());
  };

  const handleCancelEditInterval = () => {
    const updatedSchedule = new Schedule(schedule);
    updatedSchedule.deselectInterval();
    setEditableInterval(null);
    setFreeIntervals(null);
    setSchedule(updatedSchedule.getStore());
  };

  const handleSelectTime = interval => {
    const updatedSchedule = new Schedule(schedule);

    if (editableInterval && editableInterval.ScheduleID) {
      updatedSchedule.deselectInterval();
    }

    if (
      editableInterval &&
      interval.ScheduleID === editableInterval.ScheduleID
    ) {
      setEditableInterval(null);
      setFreeIntervals(null);
    } else {
      updatedSchedule.selectInterval(interval.ScheduleID, interval.Dow);
      setFreeIntervals(
        updatedSchedule.getFreeIntervals(interval.Dow, duration, interval)
      );
      setEditableInterval({ ...interval });
    }
    setSchedule(updatedSchedule.getStore());
  };


  const handleMicrosoftAuth = () => {
    setWindowRef(
      window.open(
        `${window.location.origin}/microsoft-auth`,
        'Auth',
        'status=0,toolbar=0,menubar=0,location=0,width=600,height=750'
      )
    );
  }

  const handleGoogleAuth = () => {
    setWindowRef(
      window.open(
        `${window.location.origin}/google-auth/calendar`,
        'Auth',
        'status=0,toolbar=0,menubar=0,location=0,width=600,height=750'
      )
    );
  };

  const handleDeactivateCalendar = () => {
    ConfirmDialog({
      bodyRender: <p>Are you sure you want to deactivate your calendar?</p>,
    }).then(() => {
      api.post(
        ENDPOINT.ACTION.RECRUITER_CALENDAR.DEACTIVATE,
        {},
        {
          handleError: response => {
            ResponseErrorDialog({ response });
          },
          handleSuccess: () => {
            globalActions.updateUserOption(
              USER_OPTIONS.HAS_RECRUITER_CALENDAR,
              false
            );
            setCalendar({});
            setSchedule();
            setDuration();
          },
        }
      );
    });
  };

  const handleOpenOutlookCalendar = useCallback(() => {
    window.open(
      'https://outlook.office.com/calendar/view/month',
      'Outlook Calendar'
    );
  }, []);

  const handleOpenGoogleCalendar = useCallback(() => {
    window.open(
      `https://calendar.google.com/calendar`,
      'Google Calendar'
    );
  }, []);

  const handleTimeZoneChange = useCallback((_, zone) => {
    postRequest(ENDPOINT.ACTION.RECRUITER_CALENDAR.UPDATE_SETTINGS, {
      TimeZone: zone.value,
    }, ({ calendar, duration, schedule }) => {
      if (calendar) {
        setSchedule(Schedule.prepareStore(schedule));
        setDuration(duration);
        setCalendar(calendar);
      }
    });
  }, []);

  const { publicLink, TimeZone } = calendar;

  return (
    <PageLayout
      pageClass="calendar-page"
      pageTitle="Calendar"
      mainContent={
        <div>
          <CSSTransition
            in={true}
            appear={true}
            timeout={ANIMATION_TIMEOUT_APPEAR}
            classNames="fade"
          >
            <PanelContainer
              styles={{ marginBottom: '10px', paddingTop: '35px' }}
            >
              <BasicSection
                title={!publicLink ? 'Calendar Activation' : 'Settings'}
              >
                {!publicLink && (
                  <>
                    <div className="calendar-page__row">
                      <p>
                        To activate personal calendar you must log-in into Microsoft
                        and grant required permissions to use Outlook Calendar
                        features.
                      </p>
                      <MicrosoftButton onClick={handleMicrosoftAuth}>
                        Sign in with Microsoft
                      </MicrosoftButton>
                    </div>
                    <BasicTitle text={'OR'} styles={{margin: '30px 0 10px'}} />
                    <div className="calendar-page__row">
                      <p>
                        To activate personal calendar you must log-in into Google
                        and grant required permissions to use Google Calendar
                        features.
                      </p>
                      <GoogleButton controlFunc={handleGoogleAuth} />
                    </div>
                  </>
                )}

                {publicLink && (
                  <div className="calendar-page__row">
                    <h4 className="calendar-page__subtitle">Calendar Url</h4>

                    <LinkButton
                      className="calendar-page__link"
                      text={publicLink}
                      link={publicLink}
                      target="_blank"
                      btnFontWeight="400"
                      btnFontFamily="Noto Sans"
                    />
                  </div>
                )}

                {calendar.Account && calendar.Provider === 'microsoft' && (
                  <div className="calendar-page__row">
                    <h4 className="calendar-page__subtitle">Outlook Calendar</h4>
                    <p>
                      <LinkButton
                        className="calendar-page__link"
                        text="View"
                        controlFunc={handleOpenOutlookCalendar}
                        btnFontWeight="400"
                        btnFontFamily="Noto Sans"
                      />{' '}
                      your Outlook Calendar. You connected using{' '}
                      <span className="calendar-page__accent">
                        {calendar.Account}
                      </span>{' '}
                      account. You can{' '}
                      <LinkButton
                        className="calendar-page__link"
                        text="change"
                        controlFunc={handleMicrosoftAuth}
                        btnFontWeight="400"
                        btnFontFamily="Noto Sans"
                      />{' '}
                      account or{' '}
                      <LinkButton
                        className="calendar-page__link"
                        text="deactivate"
                        controlFunc={handleDeactivateCalendar}
                        btnFontWeight="400"
                        btnFontFamily="Noto Sans"
                      />{' '}
                      calendar.
                    </p>
                  </div>
                )}

                {calendar.Account && calendar.Provider === 'google' && (
                  <div className="calendar-page__row">
                    <h4 className="calendar-page__subtitle">Google Calendar</h4>
                    <p>
                      <LinkButton
                        className="calendar-page__link"
                        text="View"
                        controlFunc={handleOpenGoogleCalendar}
                        btnFontWeight="400"
                        btnFontFamily="Noto Sans"
                      />{' '}
                      your Google Calendar. You connected using{' '}
                      <span className="calendar-page__accent">
                        {calendar.Account}
                      </span>{' '}
                      account. You can{' '}
                      <LinkButton
                        className="calendar-page__link"
                        text="change"
                        controlFunc={handleGoogleAuth}
                        btnFontWeight="400"
                        btnFontFamily="Noto Sans"
                      />{' '}
                      account or{' '}
                      <LinkButton
                        className="calendar-page__link"
                        text="deactivate"
                        controlFunc={handleDeactivateCalendar}
                        btnFontWeight="400"
                        btnFontFamily="Noto Sans"
                      />{' '}
                      calendar.
                    </p>
                  </div>
                )}

                {TimeZone && (
                  <div className="calendar-page__row">
                    <h4 className="calendar-page__subtitle">Time Zone</h4>

                    {calendar.Provider === 'microsoft' && (
                      <div style={{maxWidth: '200px', width: '100%'}}>
                        <BasicSelect onChange={handleTimeZoneChange} value={TIME_ZONES.find((zone) => {
                          return zone.value === calendar.TimeZone;
                        })} options={TIME_ZONES} isValid={true} />
                      </div>
                    )}
                    <p>{calendar.TimeZonePreview}</p>
                  </div>
                )}
              </BasicSection>
            </PanelContainer>
          </CSSTransition>

          {schedule && (
            <CSSTransition
              in={true}
              appear={true}
              timeout={ANIMATION_TIMEOUT_APPEAR}
              classNames="fade"
            >
              <PanelContainer
                styles={{ marginBottom: '10px', paddingTop: '35px' }}
              >
                <BasicSection title="Working Hours">
                  <div className="calendar-page__row">
                    <h4 className="calendar-page__subtitle">
                      Choose your working hours.
                    </h4>
                  </div>

                  <div className="calendar-page__row">
                    <div className="calendar-page__form">
                      <CalendarWorkingHoursForm
                        interval={editableInterval || null}
                        freeIntervals={freeIntervals || null}
                        actions={{
                          onDelete: handleDeleteInterval,
                          onCancel: handleCancelEditInterval,
                          onSave: handleSaveInterval,
                        }}
                      />
                    </div>
                  </div>

                  <div className="calendar-page__row calendar-page__row--working-hours">
                    <CalendarGroup
                      duration={duration}
                      schedule={schedule}
                      actions={{
                        onDayStatusChange: handleChangeDayStatus,
                        onSelectTime: handleSelectTime,
                        onAddInterval: handleAddInterval,
                      }}
                    />
                  </div>
                </BasicSection>
              </PanelContainer>
            </CSSTransition>
          )}
        </div>
      }
    />
  );
};

export default Calendar;
